import { EAction } from '@pushflow/types/poker/enums'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormatToBigBlindMultiple } from '../../../../hooks'
import { userSettingsAtom } from '../../../../store/atoms'
import { getCurrencyFractionDigits, roundFloatTo } from '../../../../utils'
import { Money } from '../../../molecules'
import type { PlayerControlsPropsType } from '../types'

export const usePlayerControlsLogic = (props: PlayerControlsPropsType) => {
  const { t } = useTranslation()
  const { formatToBigBlinds } = useFormatToBigBlindMultiple(props.bigBlind)

  const userSettings = useAtomValue(userSettingsAtom)
  const possibleActions = new Set(props.possibleActions)

  const callAmount = roundFloatTo(props.minBet - props.playerBet, 2)
  const minBet = Math.min(Math.max(props.minBet, props.bigBlind), props.maxBet)
  const minBetOrRaise = possibleActions.has(EAction.BET)
    ? minBet
    : props.minRaise

  const canBetOrRaise =
    possibleActions.has(EAction.BET) || possibleActions.has(EAction.RAISE)
  const canCheck = possibleActions.has(EAction.CHECK) && callAmount <= 0

  const clamp = (value: number, min: number, max: number) =>
    roundFloatTo(
      Math.max(Math.min(value, max), min),
      getCurrencyFractionDigits(props.currency),
    )

  const [betAmount, setBetAmount] = useState(() =>
    possibleActions.has(EAction.BET) ? minBet : props.minRaise,
  )

  const updateBet = (amount: number) => {
    setBetAmount(() => clamp(amount, minBetOrRaise, props.maxBet))
  }

  const onSliderChange = useCallback(
    (sliderValue: number) => {
      updateBet(
        userSettings.valuesInBigBlinds
          ? sliderValue * props.bigBlind
          : sliderValue,
      )
    },
    [userSettings.valuesInBigBlinds, props.bigBlind],
  )

  useEffect(() => {
    updateBet(Math.max(props.minBet, props.bigBlind))
  }, [props.minBet, props.bigBlind])

  const buttons = [
    {
      hidden: !props.possibleActions.includes(EAction.FOLD),
      label: t(`table.action.${EAction.FOLD}`),
      name: 'fold',
      onClick: () => props.onClickMove(EAction.FOLD),
    },
    {
      hidden: !canCheck && !possibleActions.has(EAction.CALL),
      label: canCheck ? (
        t(`table.action.${EAction.CHECK}`)
      ) : (
        <>
          <span>{t(`table.action.${EAction.CALL}`)}</span>
          {!possibleActions.has(EAction.CALL) || callAmount >= props.buyIn ? (
            t(`table.action.${EAction.ALL_IN}`)
          ) : (
            <Money
              isAnimated={false}
              inBigBlinds={userSettings.valuesInBigBlinds}
              bigBlind={props.bigBlind}
            >
              {callAmount}
            </Money>
          )}
        </>
      ),
      name: canCheck ? 'check' : 'call',
      onClick: () =>
        props.onClickMove(
          canCheck
            ? EAction.CHECK
            : !possibleActions.has(EAction.CALL) || callAmount >= props.buyIn
              ? EAction.ALL_IN
              : EAction.CALL,
        ),
    },
    {
      hidden: !canBetOrRaise,
      label: (
        <>
          <span>
            {possibleActions.has(EAction.BET)
              ? t(`table.action.${EAction.BET}`)
              : t(`table.action.${EAction.RAISE}`)}
          </span>
          {betAmount >= props.buyIn ? (
            t(`table.action.${EAction.ALL_IN}`)
          ) : (
            <Money
              isAnimated={false}
              inBigBlinds={userSettings.valuesInBigBlinds}
              bigBlind={props.bigBlind}
            >
              {betAmount}
            </Money>
          )}
        </>
      ),
      name: possibleActions.has(EAction.BET) ? 'bet' : 'raise',
      onClick: () =>
        props.onClickMove(
          betAmount >= props.buyIn
            ? EAction.ALL_IN
            : possibleActions.has(EAction.BET)
              ? EAction.BET
              : EAction.RAISE,
          betAmount,
        ),
    },
  ]

  return {
    buttons,
    milestoneButtons: [
      {
        label: t('table.milestone.min'),
        name: 'min',
        onClick: () => updateBet(minBetOrRaise),
      },
      {
        label: t('table.milestone.halfPot'),
        name: 'half_pot',
        onClick: () => updateBet(props.pot * 0.5),
      },
      {
        label: t('table.milestone.pot'),
        name: 'pot',
        onClick: () => updateBet(props.pot),
      },
      {
        label: t('table.milestone.max'),
        name: 'max',
        onClick: () => updateBet(props.maxBet),
      },
    ],
    betSettings: {
      show: canBetOrRaise,
      value: userSettings.valuesInBigBlinds
        ? formatToBigBlinds(betAmount)
        : betAmount,
      min: userSettings.valuesInBigBlinds
        ? formatToBigBlinds(minBetOrRaise)
        : minBetOrRaise,
      max: userSettings.valuesInBigBlinds
        ? formatToBigBlinds(props.maxBet)
        : props.maxBet,
      step: userSettings.valuesInBigBlinds ? 1 : props.bigBlind,
      showBigBlindLabel: userSettings.valuesInBigBlinds,
      onChange: onSliderChange,
    },
  }
}
